import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Badge,
  Button,
  Form,
  Grid,
  Input,
  Popconfirm,
  Popover,
  Space,
  Tag,
  Typography,
} from 'antd';
import { FilterOutlined, PlusOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import Table, { ColumnsType } from 'antd/lib/table';
import { SizeType } from 'antd/lib/config-provider/SizeContext';
import { useVT } from 'virtualizedtableforantd4';

import FourZeroThree from '../FourZeroThree';
import FiveHundred from '../FiveHundred';
import TableToolbar from '../table/TableToolbar';
import TableFooterToolbar from '../table/TableFooterToolbar';
import UsersDropdown from '../users/common/UsersDropdown';
import WxWorkChatUserModal from './WxWorkChatUserModal';

import {
  getDataWithAuthToken,
  postDataWithAuthToken,
} from '../../utils/axiosRequest';
import { alertMessage } from '../../utils/alertMessage';
import { compare, setFont } from '../../utils/colComponents';
import { hasPermission } from '../../utils/hasPermission';
import { tableScrollToTop } from '../../utils/helperFunction';
import { useLocalStorage } from '../../hooks/useLocalStorage';
import { useTab } from '../../hooks/useTab';
import { actionPermissions } from '../../constants/actionPermissions';
import { dashboardRoute } from '../../constants/pathname';
import {
  DEFAULT_FONT_SIZE,
  DEFAULT_SIZE_TYPE,
  GENERAL_TIMEOUT,
} from '../../constants/systemConstants';
import { FontSizeType, WxWorkChatUserBindingData } from '../../types';

type WxWorkChatUserTableProps = {
  /**
   * 用户详情面板调起时传入: 锁定到指定用户 (隐藏用户列/高级搜索, 仅展示该用户的绑定)。
   * 不传则为完整列表页模式。
   */
  userId?: number | string;
};

/**
 * 企业微信客户群 ↔ 商城用户 绑定表格 (通用)。
 *
 * - 列表页模式 (无 userId): 显示用户列 + 高级搜索 + 绑定编辑入口。
 * - 用户详情面板模式 (有 userId): 走 /list?userId=X, 只展示指定用户的绑定。
 */
const WxWorkChatUserTable = ({ userId }: WxWorkChatUserTableProps) => {
  const isUserDetail = userId !== undefined && userId !== null;

  const { t } = useTranslation();
  const screens = Grid.useBreakpoint();
  const [form] = Form.useForm();
  const formRef = useRef(null);
  const { addTab } = useTab();

  // page state
  const [fourZeroThree, setFourZeroThree] = useState(false);
  const [fiveHundred, setFiveHundred] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [fontSize, setFontSize] = useState<FontSizeType>(DEFAULT_FONT_SIZE);
  const [tableSize, setTableSize] = useState<SizeType>(DEFAULT_SIZE_TYPE);
  const [showModal, setShowModal] = useState(false);

  // data
  const [bindings, setBindings] = useState<WxWorkChatUserBindingData[]>([]);
  const [selectedBinding, setSelectedBinding] =
    useState<WxWorkChatUserBindingData>();

  // table state
  const [keyword, setKeyword] = useState<string>('');
  const [advance, setAdvance] = useState<{ [key: string]: any }>({});
  const [typingTimeout, setTypingTimeout] = useState<NodeJS.Timeout>();
  const [vt] = useVT(() => ({ scroll: { y: 600 } }), []);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useLocalStorage(
    'pageSize',
    '10'
  );
  const [total, setTotal] = useState(0);
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
  const [selectedRows, setSelectedRows] = useState<WxWorkChatUserBindingData[]>(
    []
  );

  const canManage = hasPermission(actionPermissions.userGroup.userManage);
  const exportFileName = 'WXWORK_CHAT_USER_LIST';

  const columnKeys: string[] = [
    'bindingId',
    'bindingToken',
    'chatId',
    'chatName',
    ...(!isUserDetail ? ['user'] : []),
    'status',
    'addTime',
    'actions',
  ];
  const [selectedColumns, setSelectedColumns] = useState<string[]>(columnKeys);

  const unbind = (bindingId: number) => {
    setIsLoading(true);
    postDataWithAuthToken(
      'wxwork/chat_user/delete',
      {},
      { params: { bindingId } }
    )
      .then((response) => {
        setIsLoading(false);
        if (response && response.goodStatus) {
          alertMessage('success', t('wxwork.alerts.bindingDeleted'));
          getBindingList();
        } else {
          alertMessage(
            'error',
            response?.msg || t('general.noResponse'),
            response?.data || undefined
          );
        }
      })
      .catch((err) => {
        console.log(err);
        setIsLoading(false);
      });
  };

  const userColumn: ColumnsType<WxWorkChatUserBindingData> = [
    {
      title: setFont(t('wxwork.user'), fontSize),
      dataIndex: 'user',
      key: 'user',
      width: 220,
      sorter: (a, b) =>
        compare(a.user ? a.user.userName : '', b.user ? b.user.userName : ''),
      render: (_: any, record: WxWorkChatUserBindingData) => (
        <Space direction="vertical" size={0}>
          <Button
            type="link"
            style={{ padding: 0, fontSize }}
            disabled={
              !record.user ||
              !hasPermission(actionPermissions.userGroup.userView)
            }
            onClick={() =>
              addTab(
                '',
                `${dashboardRoute.users.detail}?user_id=${
                  record.user ? record.user.userId : ''
                }`
              )
            }
          >
            {record.user ? record.user.userName : ''}
          </Button>
          {record.user && record.user.nickName && (
            <Typography.Text type="secondary" style={{ fontSize }}>
              {record.user.nickName}
            </Typography.Text>
          )}
          {record.user && record.user.mobilePhone && (
            <Typography.Text type="secondary" style={{ fontSize }}>
              {record.user.mobilePhone}
            </Typography.Text>
          )}
        </Space>
      ),
    },
  ];

  const columns: ColumnsType<WxWorkChatUserBindingData> = [
    {
      title: setFont(t('wxwork.bindingId'), fontSize),
      dataIndex: 'bindingId',
      key: 'bindingId',
      fixed: screens.lg ? 'left' : undefined,
      width: 80,
      render:  (text: number, record: WxWorkChatUserBindingData) => (
            <Button
              type="link"
              style={{ padding: 0, fontSize }}
              onClick={() => {
                setSelectedBinding(record);
                setShowModal(true);
              }}
              disabled={!canManage}
            >
              {text}
            </Button>
          ),
      sorter: (a, b) => compare(a.bindingId, b.bindingId),
    },
    {
      title: setFont(t('wxwork.bindingToken'), fontSize),
      dataIndex: 'bindingToken',
      key: 'bindingToken',
      render: (text: string) => (
        <Typography.Text style={{ fontSize }} copyable code>
          {text}
        </Typography.Text>
      ),
      width: 280,
    },
    {
      title: setFont(t('wxwork.chatId'), fontSize),
      dataIndex: 'chatId',
      key: 'chatId',
      render: (text: string) => (
        <Typography.Text style={{ fontSize }} copyable code>
          {text}
        </Typography.Text>
      ),
      width: 260,
    },
    {
      title: setFont(t('wxwork.chatName'), fontSize),
      dataIndex: 'chatName',
      key: 'chatName',
      render: (text: string) => setFont(text || '-', fontSize),
      width: 200,
    },
    ...(!isUserDetail ? userColumn : []),
    {
      title: setFont(t('wxwork.status'), fontSize),
      dataIndex: 'status',
      key: 'status',
      render: (status: boolean) => (
        <Tag style={{ fontSize }} color={status ? 'success' : 'default'}>
          {status ? t('wxwork.statusActive') : t('wxwork.statusInactive')}
        </Tag>
      ),
      width: 100,
    },
    {
      title: setFont(t('wxwork.addTime'), fontSize),
      dataIndex: 'addTime',
      key: 'addTime',
      render: (ts: string) => setFont(ts || '', fontSize),
      width: 180,
    },
    {
      title: setFont(t('actionsColumn.title'), fontSize),
      key: 'actions',
      fixed: screens.lg ? 'right' : undefined,
      width: isUserDetail ? 100 : 120,
      render: (record: WxWorkChatUserBindingData) => (
        <Space>
          
            <Button
              type="link"
              style={{ padding: 0, fontSize }}
              onClick={() => {
                setSelectedBinding(record);
                setShowModal(true);
              }}
              disabled={!canManage}
            >
              {t('actionsColumn.edit')}
            </Button>
         
          <Popconfirm
            title={setFont(t('actionsColumn.deleteWarning'), fontSize)}
            onConfirm={() => unbind(record.bindingId)}
            okText={t('actionsColumn.confirmation.yes')}
            cancelText={t('actionsColumn.confirmation.no')}
            placement="topRight"
            disabled={!canManage}
          >
            <Button
              danger
              type="link"
              style={{ padding: 0, fontSize }}
              disabled={!canManage}
            >
              {t('wxwork.unbind')}
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  const getBindingList = useCallback(() => {
    setIsLoading(true);
    const fv: { [key: string]: any } = formRef.current
      ? form.getFieldsValue()
      : {};
    getDataWithAuthToken('wxwork/chat_user/list', {
      params: {
        page,
        size: pageSize,
        userId: isUserDetail ? userId : fv.userId || undefined,
        chatId: keyword || fv.chatId || undefined,
        bindingToken: fv.bindingToken || undefined,
      },
    })
      .then((response) => {
        setIsLoading(false);
        if (response) {
          if (response.goodStatus) {
            setBindings(response.data.list);
            setTotal(response.data.totalItem);
            tableScrollToTop();
          } else if (response.returnCode === 403) {
            setFourZeroThree(true);
          } else {
            alertMessage(
              'error',
              response?.msg || t('general.noResponse'),
              response?.data || undefined
            );
          }
        } else setFiveHundred(true);
      })
      .catch((err) => {
        setIsLoading(false);
        console.log(err);
      });
  }, [isUserDetail, userId, page, pageSize, keyword, form, t]);

  useEffect(() => {
    getBindingList();
  }, [getBindingList]);

  // Advanced search popover (列表页模式)
  const advancedSearch = (
    <Popover
      overlayStyle={{ zIndex: 100 }}
      title={t('general.advancedSearch')}
      trigger="click"
      placement="bottomRight"
      content={
        <Form layout="vertical" form={form} ref={formRef}>
          <Form.Item name="chatId" label={t('wxwork.chatId')}>
            <Input
              allowClear
              onChange={(e) =>
                setAdvance((prev) => ({ ...prev, chatId: e.target.value }))
              }
            />
          </Form.Item>
          <Form.Item name="bindingToken" label={t('wxwork.bindingToken')}>
            <Input
              allowClear
              onChange={(e) =>
                setAdvance((prev) => ({ ...prev, bindingToken: e.target.value }))
              }
            />
          </Form.Item>
          {!isUserDetail && (
            <Form.Item name="userId" label={t('wxwork.userIdLabel')}>
              <UsersDropdown
                onChange={(value) => {
                  form.setFieldsValue({ userId: value });
                  setAdvance((prev) => ({ ...prev, userId: value }));
                }}
              />
            </Form.Item>
          )}
          <Form.Item style={{ marginBottom: 12 }}>
            <Space>
              <Button
                type="primary"
                onClick={() => {
                  setKeyword('');
                  if (page !== 1) setPage(1);
                  else {
                    if (typingTimeout) clearTimeout(typingTimeout);
                    setTypingTimeout(
                      setTimeout(() => getBindingList(), GENERAL_TIMEOUT)
                    );
                  }
                }}
              >
                {t('general.search')}
              </Button>
              <Button
                disabled={
                  !advance.chatId && !advance.userId && !advance.bindingToken
                }
                onClick={() => {
                  form.resetFields();
                  setAdvance({});
                  if (page !== 1) setPage(1);
                  else getBindingList();
                }}
              >
                {t('general.reset')}
              </Button>
            </Space>
          </Form.Item>
        </Form>
      }
    >
      <Badge
        count={Object.keys(advance).reduce((accumulator, obj) => {
          if (advance[obj as keyof typeof advance]) {
            return accumulator + 1;
          }
          return accumulator;
        }, 0)}
      >
        <Button icon={<FilterOutlined />}>{t('general.advancedSearch')}</Button>
      </Badge>
    </Popover>
  );

  if (fourZeroThree) return <FourZeroThree />;
  if (fiveHundred) return <FiveHundred />;

  return (
    <>
      <Typography.Title
        level={isUserDetail ? 4 : 3}
        style={{ fontWeight: 500, marginBottom: isUserDetail ? 12 : undefined }}
      >
        {t('wxwork.chatBindingsTitle')}
      </Typography.Title>
      <TableToolbar
        leftElement={
          <Button
            icon={<PlusOutlined />}
            disabled={!canManage}
            onClick={() => {
              setSelectedBinding(undefined);
              setShowModal(true);
            }}
          >
            {t('wxwork.bindAnother')}
          </Button>
        }
        setFontSize={setFontSize}
        fontSize={fontSize}
        tableSize={tableSize}
        setTableSize={setTableSize}
        refresh={() => getBindingList()}
        totalItems={total}
        searchPlaceholder={t('wxwork.searchPlaceholder')}
        search={(kw) => {
          setKeyword(kw);
          setPage(1);
        }}
        rows={bindings}
        columns={columns}
        columnKeys={columnKeys}
        selectedColumns={selectedColumns}
        setSelectedColumns={setSelectedColumns}
        exportConfig={{ fileName: exportFileName }}
        advancedSearch={advancedSearch}
      />
      <Table<WxWorkChatUserBindingData>
        columns={columns.filter((x) =>
          selectedColumns.includes(x.key?.toString() ?? '')
        )}
        dataSource={bindings}
        components={vt}
        scroll={{ y: isUserDetail ? 400 : 600, x: isUserDetail ? 1000 : 1200 }}
        size={tableSize}
        loading={isLoading}
        pagination={{
          total,
          showQuickJumper: true,
          showSizeChanger: true,
          showTotal: (total, range) =>
            t('general.paginationTotal', {
              start: range[0],
              end: range[1],
              total,
            }),
          size: 'small',
          defaultPageSize: pageSize,
          onChange: (p, pSize) => {
            setPage(p);
            setPageSize(pSize || pageSize);
            setSelectedRowKeys([]);
          },
          current: page,
        }}
        rowKey={(b) => b.bindingId}
        rowSelection={{
          selectedRowKeys,
          hideSelectAll: true,
          onChange: (keys, rows) => {
            setSelectedRowKeys(keys);
            setSelectedRows(rows);
          },
        }}
      />
      {!!selectedRowKeys.length && (
        <TableFooterToolbar
          selectedRows={selectedRows}
          setSelectedRowKeys={setSelectedRowKeys}
          columns={columns.filter((x) =>
            selectedColumns.includes(x.key?.toString() ?? '')
          )}
          funct={{ exportConfig: { fileName: exportFileName } }}
        />
      )}
      <WxWorkChatUserModal
        visible={showModal}
        setVisible={setShowModal}
        binding={selectedBinding}
        lockedUserId={isUserDetail ? Number(userId) : undefined}
        callBack={getBindingList}
      />
    </>
  );
};

export default WxWorkChatUserTable;
